import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { AiSkillScope, SkillTemplate } from '@seen/core/models/skillTemplate'
import {
  ActivityComment,
  ActivityEntry,
  useResourceVersions,
  useSearch,
  useVersionOriginTask,
} from '@seen/react'
import { orderBy, sortBy, truncate } from 'lodash-es'
import type { JSX, ReactNode } from 'react'

export interface AiSkillActivityEntry {
  action: ReactNode
  skill: SkillTemplate
  key: string
}

export function getAiSkillActivity(
  skills: SkillTemplate[]
): AiSkillActivityEntry[] {
  const sortedSkills = sortBy(skills, (skill) => skill.lastUpdated)

  return sortedSkills.flatMap((skill, idx) => {
    if (idx === 0) {
      return [
        {
          action: 'created the skill',
          skill,
          key: `${skill.versionId}-created`,
        },
      ]
    }

    const previousSkill = sortedSkills[idx - 1]
    const entries: AiSkillActivityEntry[] = []

    if (skill.title !== previousSkill.title) {
      entries.push({
        action: `changed title from ${quoteValue(previousSkill.title)} to ${quoteValue(skill.title)}`,
        skill,
        key: `${skill.versionId}-title`,
      })
    }

    if (skill.description !== previousSkill.description) {
      entries.push({
        action: skill.description
          ? previousSkill.description
            ? 'updated description'
            : 'added description'
          : 'removed description',
        skill,
        key: `${skill.versionId}-description`,
      })
    }

    if (skill.scope !== previousSkill.scope) {
      entries.push({
        action: `changed scope from ${scopeLabel(previousSkill.scope)} to ${scopeLabel(skill.scope)}`,
        skill,
        key: `${skill.versionId}-scope`,
      })
    }

    if (skill.content !== previousSkill.content) {
      entries.push({
        action: skill.content
          ? previousSkill.content
            ? 'updated content'
            : 'added content'
          : 'cleared content',
        skill,
        key: `${skill.versionId}-content`,
      })
    }

    if (skill.status !== previousSkill.status) {
      entries.push({
        action:
          skill.status === 'retired'
            ? 'deleted the skill'
            : `changed status to ${skill.status}`,
        skill,
        key: `${skill.versionId}-status`,
      })
    }

    if (entries.length > 0) {
      return entries
    }

    return [
      {
        action: 'updated the skill',
        skill,
        key: `${skill.versionId}-updated`,
      },
    ]
  })
}

export function AiSkillActivity({
  skill,
}: {
  skill: SkillTemplate
}): JSX.Element {
  const { data: pastSkills = [] } = useResourceVersions(SkillTemplate, skill.id)
  const skillActivity = getAiSkillActivity(pastSkills)
  const skillOriginOf = useVersionOriginTask(pastSkills)

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([skill.reference])
  )

  const all = orderBy(
    [...skillActivity, ...comments],
    (entry) => {
      if (entry instanceof CommentCommunication) {
        return entry.sent
      }
      return entry.skill.lastUpdated
    },
    ['desc']
  )

  return (
    <>
      {all.map((entry) => {
        if (entry instanceof CommentCommunication) {
          return (
            <ActivityComment key={entry.reference.reference} comment={entry} />
          )
        }

        return (
          <ActivityEntry
            key={entry.key}
            author={entry.skill.resourceAuthor}
            time={entry.skill.lastUpdated}
            action={entry.action}
            originTask={skillOriginOf(entry.skill)}
          />
        )
      })}
    </>
  )
}

function quoteValue(value: string): string {
  return `"${truncate(value || 'Untitled', { length: 48 })}"`
}

function scopeLabel(scope: SkillTemplate['scope']): string {
  return scope === AiSkillScope.Shared ? 'Workspace' : 'Personal'
}
